<mat-sidenav-container class="sidenav-container">

    <!-- Actions Sidenav using the new reusable component -->
    <mat-sidenav mode="side" opened position="start" class="actions-sidenav">
        <app-entity-actions-sidenav
            title="Party Actions"
            [actions]="sidenavActions"
            [tabs]="sidenavTabs"
            [selectedTabIndex]="selectedIndex"
            (tabSelected)="selectTab($event)">
        </app-entity-actions-sidenav>
    </mat-sidenav>

    <mat-sidenav-content>
        <div class="back-button-section">
            <button mat-button (click)="backToPartyList()">
                <mat-icon>arrow_back</mat-icon>
                Back to Party List
            </button>
        </div>
        @if (currentVendor?.isInactive) {
            <div class="inactive-banner">
                <mat-icon>block</mat-icon>
                <span>This vendor is <strong>inactive</strong> in NetSuite.</span>
            </div>
        }
        <div class="header-and-status-section">
            <div class="vendor-header-section">
                <div class="vendor-name">
                    <span class="vendor-name-text">{{ currentVendor?.name }}</span>
                    @if (currentVendor?.sourceSystem === 'netsuite') {
                        <span class="netsuite-badge">NetSuite</span>
                    }
                </div>
            </div>
            <div class="vendor-status-buttons">
                @if (currentVendor?.baaRequired) {
                    <button mat-stroked-button color="warn">
                        Missing BAA
                    </button>
                }
                @if (currentVendor?.coiRequired) {
                    <button mat-stroked-button color="warn">
                        Missing COI
                    </button>
                }
            </div>
        </div>

        <div class="vendor-details-container">
            <div class="vendor-details-section">
                <div class="detail-item">
                    <div class="detail-label">Party Number</div>
                    <div class="detail-value">{{ currentVendor?.vendorNum }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">GL #</div>
                    <div class="detail-value">{{ currentVendor?.glNumber || currentVendor?.netsuiteEntityId || '-' }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Legal Name</div>
                    <div class="detail-value">{{ currentVendor?.name }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Accounting Company Name</div>
                    <div class="detail-value">{{ accountingCompanyName || '-' }}</div>
                </div>
                <!-- The two company-name values the NetSuite sync actually carries. They were
                     synced all along but never declared on the Party model or bound anywhere,
                     so "Accounting Company Name" above read blank while these sat unused.
                     Shown as what they ARE rather than folded into the field above: NetSuite is
                     not consistent about which holds the practice and which the person (the two
                     live contractors are exact mirrors), so picking one would put a
                     plausible-looking wrong name on roughly half the contractors. Only rendered
                     when populated, which in practice means NetSuite-sourced parties. -->
                @if (currentVendor?.companyName) {
                  <div class="detail-item">
                      <div class="detail-label">Company Name (NetSuite)</div>
                      <div class="detail-value">{{ currentVendor?.companyName }}</div>
                  </div>
                }
                @if (currentVendor?.legalName) {
                  <div class="detail-item">
                      <div class="detail-label">Legal Name (NetSuite)</div>
                      <div class="detail-value">{{ currentVendor?.legalName }}</div>
                  </div>
                }
                <div class="detail-item">
                    <div class="detail-label">DBA</div>
                    <div class="detail-value">{{ currentVendor?.dba }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Party Type</div>
                    <div class="detail-value">{{ currentVendor?.partyType }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Standing</div>
                    <div class="detail-value">{{ currentVendor?.standing }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Address</div>
                    <div class="detail-value">{{ currentVendor?.address }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">City</div>
                    <div class="detail-value">{{ currentVendor?.city }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">State</div>
                    <div class="detail-value">{{ currentVendor?.state }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Zip</div>
                    <div class="detail-value">{{ currentVendor?.zip }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Primary Phone</div>
                    <div class="detail-value">{{ currentVendor?.phonePrimary }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Fax</div>
                    <div class="detail-value">{{ currentVendor?.fax }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Website</div>
                    <div class="detail-value">{{ currentVendor?.website }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">TIN</div>
                    <div class="detail-value">{{ currentVendor?.tin }}</div>
                </div>
                <div class="detail-item">
                    <div class="detail-label">Specialty</div>
                    <div class="detail-value">{{ currentVendor?.specialty }}</div>
                </div>
            </div>
        </div>

        <!-- Parent/Child Relationships Section -->
        @if (parentParty || childParties.length > 0) {
          <div class="relationships-section">
            @if (parentParty) {
              <div class="relationship-card">
                <div class="relationship-header">
                  <mat-icon>account_tree</mat-icon>
                  <span>Parent Party</span>
                </div>
                <div class="relationship-content">
                  <a class="party-link" (click)="navigateToParty(parentParty.vendorNum)">
                    {{ parentParty.name }}
                  </a>
                </div>
              </div>
            }
            @if (childParties.length > 0) {
              <div class="relationship-card">
                <div class="relationship-header">
                  <mat-icon>account_tree</mat-icon>
                  <span>Child Parties ({{ childParties.length }})</span>
                </div>
                <div class="relationship-content">
                  @for (child of childParties; track child.vendorNum) {
                    <a class="party-link" (click)="navigateToParty(child.vendorNum)">
                      {{ child.name }}
                    </a>
                  }
                </div>
              </div>
            }
          </div>
        }

        @if (waitingForServer) {
            <div class="loading-spinner">
                <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
            </div>
        }
        
<div class="tab-content-wrapper">
    <mat-tab-group
        [selectedIndex]="selectedIndex"
        (selectedIndexChange)="selectTab($event)">

        <mat-tab label="Contracts">
            <div class="tab-content">
              @if (partyContracts.length > 0) {
                <app-contract-connections
                  [selectedContractList]="partyContracts">
                </app-contract-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>description</mat-icon>
                    <p>No contracts associated with this party</p>
                  </mat-card-content>
                </mat-card>
              }
            </div>
        </mat-tab>

        <mat-tab label="Parties">
            <h3>Parties</h3>
            <p>Related parties will be displayed here.</p>
        </mat-tab>

        <mat-tab label="People">
            <h3>People</h3>
            <p>People associated with this party will be displayed here.</p>
        </mat-tab>

        <mat-tab label="BAAs">
            <div class="tab-content">
              @if (partyBaas.length > 0) {
                <app-baa-connections
                  [selectedBaaList]="partyBaas">
                </app-baa-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>policy</mat-icon>
                    <p>No BAAs associated with this party</p>
                  </mat-card-content>
                </mat-card>
              }
            </div>
        </mat-tab>

        <mat-tab label="COIs">
            <div class="tab-content">
              @if (partyCois.length > 0) {
                <app-coi-connections
                  [selectedCoiList]="partyCois">
                </app-coi-connections>
              } @else {
                <mat-card class="empty-state">
                  <mat-card-content>
                    <mat-icon>verified_user</mat-icon>
                    <p>No COIs associated with this party</p>
                  </mat-card-content>
                </mat-card>
              }
            </div>
        </mat-tab> 

<mat-tab label="Details">
  <div class="vendor-details-container">
    <div class="card-container">
      <section class="section-group">
        <h2 class="section-title">General Information</h2>
        <div class="display-grid">
          <div class="detail-item">
            <div class="detail-label">GL #</div>
            <div class="detail-value">{{ currentVendor?.glNumber || currentVendor?.netsuiteEntityId || '-' }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Annual Revenues</div>
            <div class="detail-value">{{ currentVendor?.annualRevenues }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Number of Employees</div>
            <div class="detail-value">{{ currentVendor?.numEmployees }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">DUNS #</div>
            <div class="detail-value">{{ currentVendor?.dunsNum }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">UPIN #</div>
            <div class="detail-value">{{ currentVendor?.upinNum }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">NPI #</div>
            <div class="detail-value">{{ currentVendor?.npiNum }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">CCN #</div>
            <div class="detail-value">{{ currentVendor?.ccnNum }}</div>
          </div>
        </div>
      </section>

      <section class="section-group">
        <h2 class="section-title">Company Details</h2>
        <div class="display-grid">
          <div class="detail-item">
            <div class="detail-label">Public or Private</div>
            <div class="detail-value">{{ currentVendor?.publicOrPrivate }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Non-Profit or For Profit</div>
            <div class="detail-value">{{ currentVendor?.nonOrForProfit }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">State of Incorporation</div>
            <div class="detail-value">{{ currentVendor?.stateOfIncorporation }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Tax Exempt Certificate #</div>
            <div class="detail-value">{{ currentVendor?.taxExemptNum }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Formerly Known As (FKA)</div>
            <div class="detail-value">{{ currentVendor?.formallyKnownAs }}</div>
          </div>
        </div>
      </section>

      <section class="section-group">
        <h2 class="section-title">Secondary Address</h2>
        <div class="display-grid">
          <div class="detail-item">
            <div class="detail-label">Secondary Address</div>
            <div class="detail-value">{{ currentVendor?.secondaryAddress }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Secondary City</div>
            <div class="detail-value">{{ currentVendor?.secondaryCity }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Secondary State</div>
            <div class="detail-value">{{ currentVendor?.secondaryState }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Secondary Zip</div>
            <div class="detail-value">{{ currentVendor?.secondaryZip }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Country</div>
            <div class="detail-value">{{ currentVendor?.country }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">County</div>
            <div class="detail-value">{{ currentVendor?.county }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Secondary Phone</div>
            <div class="detail-value">{{ currentVendor?.phoneSecondary }}</div>
          </div>
        </div>
      </section>

    </div>
  </div>
  <section class="section-group">
        <h2 class="section-title">Team Assignment</h2>
        <div class="display-grid">
          <div class="detail-item">
            <div class="detail-label">Owner</div>
            <div class="detail-value">{{ getOwnerDisplayName(currentVendor?.owner) }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Assessor</div>
            <div class="detail-value">{{ getOwnerDisplayName(currentVendor?.assessor) }}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Final Reviewer</div>
            <div class="detail-value">{{ getOwnerDisplayName(currentVendor?.finalReviewer) }}</div>
          </div>
        </div>
      </section>
</mat-tab>
        <mat-tab label="Description"> 
            <h3>Description</h3> 
            <div class="read-only-editor-content" [innerHTML]="currentVendor?.description"></div> 
        </mat-tab> 

        <mat-tab label="Attachments">
          <h2>Attachments for Party</h2>
          <app-attachment-connections
            [attachmentObjectArray]="entityAttachments"
            (attachmentUpdated)="refreshAttachments()">
          </app-attachment-connections>
        </mat-tab> 

        <mat-tab label="NMC"> 
            <h3>NMC</h3> 
            <p>This tab displays NMC data in a read-only format.</p> 
        </mat-tab> 

        <mat-tab label="Assessments">
          <app-assessment-connections
            [entityId]="currentVendor?.vendorNum || ''"
            entityType="party"
            [sourceRecord]="currentVendor?.name || ''">
          </app-assessment-connections>
        </mat-tab>

        <mat-tab label="Dates">
          <h2>Dates for Party</h2>
          <app-critical-date-connections
            [dateObjectArray]="entityDates"
            (criticalDateUpdated)="refreshCriticalDates()">
          </app-critical-date-connections>
        </mat-tab>

        <mat-tab label="Notes">
          <app-notes
            [notes]="currentVendor?.notes || []"
            (notesSaved)="saveNotes($event)">
          </app-notes>
        </mat-tab>

        <!-- Tab 12: Timecards -->
        <mat-tab label="Timecards">
          <div class="tab-content">

            @if (!isChildParty) {
              <!-- Empty state — only child parties (under a parent org) can enable timecards.
                   Heading is the call-to-action so admins who skim past it still know the next step. -->
              <div class="timecard-enable-container">
                <mat-icon class="timecard-hero-icon">account_tree</mat-icon>
                <h2 class="timecard-hero-title">Set a parent organization to enable Timecards</h2>
                <p class="timecard-hero-description">
                  Timecards are for HCPs who work under a parent organization (a hospital, practice,
                  or facility). This party doesn't have a parent set, so it isn't eligible yet.
                </p>
                <p class="timecard-hero-description">
                  Open this party for editing, set its <strong>Parent Vendor</strong> field, and
                  save — the Enable Timecards toggle will appear here afterwards.
                </p>
                <div class="timecard-toggle-area">
                  <button mat-raised-button color="primary" type="button" (click)="editParty()">
                    <mat-icon>edit</mat-icon>
                    Edit Party
                  </button>
                </div>
              </div>
            } @else if (timecardCreating) {
              <!-- Creating state -->
              <div class="timecard-loading">
                <mat-spinner diameter="48"></mat-spinner>
                <p>Creating timecard specification...</p>
              </div>
            } @else if (!currentVendor?.timecardsEnabled && !linkedTimecardSpec) {
              <!-- Not enabled — show big toggle (gated when identity is incomplete) -->
              <div class="timecard-enable-container">
                <mat-icon class="timecard-hero-icon">schedule</mat-icon>
                <h2 class="timecard-hero-title">Timecard Tracking</h2>
                <p class="timecard-hero-description">
                  Enable timecards for this HCP to manage shift logging, credential and position
                  tracking, and approval workflow.
                </p>

                @if (missingTimecardsFields.length > 0) {
                  <!-- Capability gate: identity must be complete before timecards can be enabled. -->
                  <div class="timecard-gate-banner" role="status" aria-live="polite">
                    <mat-icon class="gate-icon">warning_amber</mat-icon>
                    <div class="gate-content">
                      <h3 class="gate-heading">Timecards can't be enabled for this party yet</h3>
                      <p class="gate-body">
                        Add a first name and last name before turning timecards on. The mobile app
                        shows the HCP's name on every timecard.
                      </p>
                      <p class="gate-missing">
                        <span class="gate-missing-label">Missing:</span>
                        <span class="gate-missing-list">{{ missingTimecardsFields.join(', ') }}</span>
                      </p>
                      <div class="gate-actions">
                        <button mat-stroked-button color="primary" type="button" (click)="editParty()">
                          <mat-icon>edit</mat-icon>
                          Edit Party
                        </button>
                      </div>
                    </div>
                  </div>
                }

                @if (canEnableTimecards && !effectiveEmail) {
                  <p class="timecard-hero-description">
                    This party has no email on file. When you turn timecards on, you'll be asked for the
                    email this person will sign in with. It's saved on the timecard spec; the party isn't changed.
                  </p>
                }

                <div class="timecard-toggle-area"
                     [class.toggle-area-disabled]="!canEnableTimecards"
                     [matTooltip]="!canEnableTimecards ? 'Add the missing identity fields first.' : ''"
                     matTooltipPosition="above">
                  <mat-slide-toggle
                    [checked]="false"
                    [disabled]="!canEnableTimecards"
                    (change)="enableTimecards()"
                    color="primary"
                    class="timecard-toggle">
                    <span class="toggle-label">Enable Timecards for This Party</span>
                  </mat-slide-toggle>
                </div>

                @if (canEnableTimecards) {
                  <p class="timecard-warning">
                    <mat-icon>info</mat-icon>
                    Once enabled, a timecard specification will be created and linked to this party.
                    This action cannot be undone.
                  </p>
                }
              </div>
            } @else {
              <!-- Enabled — show linked timecard info -->
              <div class="timecard-enabled-container">
                <div class="timecard-status-banner">
                  <mat-icon class="status-icon">check_circle</mat-icon>
                  <div class="status-text">
                    <h3>Timecards Enabled</h3>
                    <p>This party is linked to a timecard specification.</p>
                  </div>
                </div>

                @if (linkedTimecardSpec) {
                  <div class="timecard-identity-preview">
                    <h4>Identity</h4>
                    <div class="identity-row">
                      <span class="identity-label">Name:</span>
                      <span class="identity-value">{{ linkedTimecardSpec.firstName }} {{ linkedTimecardSpec.lastName }}</span>
                    </div>
                    <div class="identity-row">
                      <span class="identity-label">Email:</span>
                      <span class="identity-value">{{ linkedTimecardSpec.email }}</span>
                    </div>
                  </div>

                  <div class="timecard-actions">
                    <button mat-stroked-button color="primary" (click)="editTimecardSpec()">
                      <mat-icon>edit</mat-icon>
                      Edit Timecard Spec
                    </button>
                  </div>
                }
              </div>
            }

          </div>
        </mat-tab>
    </mat-tab-group>
</div>

        <!-- Collapsible Integrations Section -->
        <mat-accordion class="integrations-accordion">
          <mat-expansion-panel>
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon>sync</mat-icon>
                <span>Integrations</span>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <!-- Tax & Compliance -->
            <section class="section-group">
              <h3 class="subsection-title">Tax & Compliance</h3>
              <div class="display-grid">
                <div class="detail-item">
                  <div class="detail-label">FEIN</div>
                  <div class="detail-value">{{ currentVendor?.fein || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">G/L Account Number</div>
                  <div class="detail-value">{{ currentVendor?.glAccountNumber || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Business Structure</div>
                  <div class="detail-value">{{ currentVendor?.businessStructure || '-' }}</div>
                </div>
                @if (currentVendor?.sourceSystem === 'netsuite') {
                  <div class="detail-item">
                    <div class="detail-label">NetSuite BAA flag</div>
                    <div class="detail-value">{{ currentVendor?.netsuiteBaa ? 'Yes' : 'No' }}</div>
                  </div>
                }
                <div class="detail-item">
                  <div class="detail-label">License Type</div>
                  <div class="detail-value">{{ currentVendor?.licenseType || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">License Number</div>
                  <div class="detail-value">{{ currentVendor?.licenseNumber || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">License State</div>
                  <div class="detail-value">{{ currentVendor?.licenseState || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Diversity Standing</div>
                  <div class="detail-value">{{ currentVendor?.diversityStanding?.length ? currentVendor.diversityStanding.join(', ') : '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">1099 Eligible</div>
                  <div class="detail-value">{{ currentVendor?.is1099Eligible ? 'Yes' : 'No' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">1099 Category</div>
                  <div class="detail-value">{{ currentVendor?.category1099 || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Payment Terms</div>
                  <div class="detail-value">{{ currentVendor?.paymentTerms || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">DEA 542</div>
                  <div class="detail-value">{{ currentVendor?.de542 ? 'Yes' : 'No' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">OIG Screening</div>
                  <div class="detail-value">{{ currentVendor?.oigScreening ? 'Yes' : 'No' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Ancillary</div>
                  <div class="detail-value">{{ currentVendor?.ancillary ? 'Yes' : 'No' }}</div>
                </div>
              </div>
            </section>

            <!-- W9 Address -->
            <section class="section-group">
              <h3 class="subsection-title">W9 Address</h3>
              <div class="display-grid">
                <div class="detail-item">
                  <div class="detail-label">W9 Street Address 1</div>
                  <div class="detail-value">{{ currentVendor?.w9StreetAddress1 || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">W9 Street Address 2</div>
                  <div class="detail-value">{{ currentVendor?.w9StreetAddress2 || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">W9 City</div>
                  <div class="detail-value">{{ currentVendor?.w9City || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">W9 State</div>
                  <div class="detail-value">{{ currentVendor?.w9State || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">W9 ZIP</div>
                  <div class="detail-value">{{ currentVendor?.w9Zip || '-' }}</div>
                </div>
              </div>
            </section>

            <!-- Invoice Remittance Address -->
            <section class="section-group">
              <h3 class="subsection-title">Invoice Remittance Address</h3>
              <div class="display-grid">
                <div class="detail-item">
                  <div class="detail-label">Invoice Remittance Street Address 1</div>
                  <div class="detail-value">{{ currentVendor?.remittanceStreetAddress1 || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Invoice Remittance Street Address 2</div>
                  <div class="detail-value">{{ currentVendor?.remittanceStreetAddress2 || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Invoice Remittance City</div>
                  <div class="detail-value">{{ currentVendor?.remittanceCity || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Invoice Remittance State</div>
                  <div class="detail-value">{{ currentVendor?.remittanceState || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Invoice Remittance ZIP</div>
                  <div class="detail-value">{{ currentVendor?.remittanceZip || '-' }}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Remit To</div>
                  <div class="detail-value">{{ currentVendor?.printCheckAs || '-' }}</div>
                </div>
              </div>
            </section>

            <!-- NetSuite Identity & Classification (NetSuite-sourced, read-only) -->
            @if (currentVendor?.sourceSystem === 'netsuite') {
              <section class="section-group">
                <h3 class="subsection-title">NetSuite Identity &amp; Classification</h3>
                <div class="display-grid">
                  <div class="detail-item">
                    <div class="detail-label">Salutation</div>
                    <div class="detail-value">{{ currentVendor?.salutation || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Middle Name</div>
                    <div class="detail-value">{{ currentVendor?.middleName || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Title</div>
                    <div class="detail-value">{{ currentVendor?.title || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Vendor Type</div>
                    <div class="detail-value">{{ currentVendor?.vendorType || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Approval Status</div>
                    <div class="detail-value">{{ currentVendor?.approvalStatus || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Account Number</div>
                    <div class="detail-value">{{ currentVendor?.accountNumber || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Business Number</div>
                    <div class="detail-value">{{ currentVendor?.businessNumber || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">NetSuite Date Created</div>
                    <div class="detail-value">{{ currentVendor?.netsuiteDateCreated || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">VAT Reg. Number</div>
                    <div class="detail-value">{{ currentVendor?.vatRegNumber || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Confidential</div>
                    <div class="detail-value">{{ currentVendor?.confidential ? 'Yes' : 'No' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">AR Check Tracking</div>
                    <div class="detail-value">{{ currentVendor?.arCheckTracking ? 'Yes' : 'No' }}</div>
                  </div>
                </div>
              </section>

              <!-- NetSuite Contact (NetSuite-sourced, read-only) -->
              <section class="section-group">
                <h3 class="subsection-title">NetSuite Contact</h3>
                <div class="display-grid">
                  <div class="detail-item">
                    <div class="detail-label">Email</div>
                    <div class="detail-value">{{ currentVendor?.email || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Alternative Email</div>
                    <div class="detail-value">{{ currentVendor?.alternativeEmail || currentVendor?.paymentNotificationEmail || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Phone</div>
                    <div class="detail-value">{{ currentVendor?.phone || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Mobile Phone</div>
                    <div class="detail-value">{{ currentVendor?.mobilePhone || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Home Phone</div>
                    <div class="detail-value">{{ currentVendor?.homePhone || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Alternate Phone</div>
                    <div class="detail-value">{{ currentVendor?.altPhone || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Fax</div>
                    <div class="detail-value">{{ currentVendor?.fax || '-' }}</div>
                  </div>
                  <div class="detail-item">
                    <div class="detail-label">Website (URL)</div>
                    <div class="detail-value">{{ currentVendor?.url || '-' }}</div>
                  </div>
                </div>
              </section>
            }

          </mat-expansion-panel>
        </mat-accordion>

    </mat-sidenav-content>
</mat-sidenav-container>